import {
  EntityDetailSchema,
  EntityRootData,
  EntityDetail,
  EntityDetailSchemaWithRelationships,
  LocalDataSourceAnnotation,
  RelationshipDataSourceAnnotation,
  UrlDataSourceAnnotation,
  DataSourceRegistryItemAnnotation,
  MergedDataSourceAnnotation,
} from '@web-config-app/core';
import { getRelationshipSchemaItemType } from '@web-config-app/schema-utils';
import { cloneDeep } from 'lodash-es';
import jp from 'jsonpath';
import { isUsingConfigAppBackend } from '@web-config-app/common';
import type { DataSource } from '../../types/data-source';

/**
 * This will limit the data source to include ONLY entities of the type
 * defined in the linked relationship
 */

const getIncludedDataSourceForRelationship = (
  includedEntities: EntityDetail[] | undefined,
  relationship: string | undefined,
  schema: EntityDetailSchemaWithRelationships,
) => {
  if (!relationship) return includedEntities;
  const { type } = getRelationshipSchemaItemType(relationship, schema);
  return includedEntities?.filter(
    (entity) =>
      entity.type === type ||
      entity.attributes?.entityMetadata?.domainEntityType === type,
  );
};

type BaseDataSource =
  | LocalDataSourceAnnotation
  | RelationshipDataSourceAnnotation;

const isLocalDataSource = (
  dataSource: DataSourceRegistryItemAnnotation,
): dataSource is LocalDataSourceAnnotation => 'path' in dataSource;

const isRelationshipDataSource = (
  dataSource: DataSourceRegistryItemAnnotation,
): dataSource is RelationshipDataSourceAnnotation =>
  'relationship' in dataSource;

const isUrlDataSource = (
  dataSource: DataSourceRegistryItemAnnotation,
): dataSource is UrlDataSourceAnnotation => 'url' in dataSource;

const isMergedDataSource = (
  dataSource: DataSourceRegistryItemAnnotation,
): dataSource is MergedDataSourceAnnotation => 'mergeSources' in dataSource;

const isBaseDataSource = (
  dataSource: DataSourceRegistryItemAnnotation,
): dataSource is BaseDataSource =>
  isLocalDataSource(dataSource) || isRelationshipDataSource(dataSource);

/**
 * Adjusts the path based on whether the tenant is using config app backend
 * For config app backend, the data is under attributes.domainEntityAttributes
 * For other backends, the data is directly under attributes
 */
const getAdjustedPath = (
  path: string,
  isEntityUsingConfigAppBackend?: boolean,
) => {
  // If the path already starts with $.attributes, we need to adjust it
  if (
    path.startsWith('$.attributes') &&
    isUsingConfigAppBackend({
      entityLevelOverride: isEntityUsingConfigAppBackend,
    })
  ) {
    return path.replace('$.attributes', '$.attributes.domainEntityAttributes');
  }
  return path;
};

/**
 * `jp.value` returns only the first match. For paths with `[*]` wildcards
 * (e.g., `$.sections[*].questions[*]`) we want every match aggregated as
 * an array — `jp.query` does that.
 */
const evaluatePath = (data: unknown, path: string): any =>
  path.includes('[*]') ? jp.query(data, path) : jp.value(data, path);

/**
 * Resolves a local data source's `path` against the entity data. Mirrors
 * the behavior of the legacy `getLocalDataSourceValue`: try the
 * config-app-backend-adjusted path first, then fall back to the original
 * path if the adjusted lookup misses. The fallback exists because some
 * tests / legacy entities don't have a `domainEntityAttributes` wrapper.
 */
const getLocalDataSourceValue = (
  data: EntityRootData | undefined,
  path: string,
  isEntityUsingConfigAppBackend?: boolean,
) => {
  const sourceData = data ?? {};
  const adjustedPath = getAdjustedPath(path, isEntityUsingConfigAppBackend);
  const adjustedValue = evaluatePath(sourceData, adjustedPath);

  // An empty array only counts as a miss for WILDCARD paths, where
  // `jp.query` returns [] both for "no matches yet" and "wrong wrapper
  // path" and we can't tell which. For a non-wildcard path, [] is the
  // property's real value (e.g., a draft campaign's empty
  // `campaignNodes`) — falling back would turn it into `undefined` and
  // make DataSourceEnumControl throw.
  const isWildcard = path.includes('[*]');
  const isMissing =
    adjustedValue === undefined ||
    (isWildcard && Array.isArray(adjustedValue) && adjustedValue.length === 0);

  if (adjustedPath !== path && isMissing) {
    return evaluatePath(sourceData, path);
  }
  return adjustedValue;
};

/**
 * Get any data sources that have been declared in the entity schema's root using
 * the {@link EntityDataSourcesAnnotation} annotation
 */

export const getSchemaDataSources = (
  {
    'x-entity-data-source-registry': dataSourceRegistry,
    ...schema
  }: EntityDetailSchema,
  data: EntityRootData | undefined,
  includedEntities?: EntityDetail[],
  isEntityUsingConfigAppBackend?: boolean,
) => {
  const allSources = dataSourceRegistry?.sources ?? [];
  const mergedSources = allSources.filter(isMergedDataSource);
  const compiledBaseSources: Array<DataSource> = allSources
    .filter(isBaseDataSource)
    .map((source) =>
      isRelationshipDataSource(source)
        ? {
            name: source.name,
            config: source,
            type: 'entity',
            value: getIncludedDataSourceForRelationship(
              includedEntities,
              source.relationship,
              schema,
            ),
          }
        : {
            name: source.name,
            type: 'entity',
            config: source,
            value: cloneDeep(
              getLocalDataSourceValue(
                data,
                source.path,
                isEntityUsingConfigAppBackend,
              ),
            ),
          },
    );

  const urlDataSources: Array<DataSource> = allSources
    .filter(isUrlDataSource)
    .map((source) => ({
      name: source.name,
      config: source,
      value: source.url,
      type: 'url',
    }));

  const compiledMergedSources: Array<DataSource> = mergedSources.map(
    (source) => {
      const {
        baseSource: baseSourceName,
        mergeSources,
        name: mergeSourceName,
      } = source;
      const baseSource = compiledBaseSources.find(
        (mergeSource) => mergeSource.name === baseSourceName,
      );

      const sourcesData = mergeSources
        .filter((mergeSource) => mergeSource.dataSource !== baseSourceName)
        .map(
          ({
            dataSource: mergeDataSourceName,
            identifier,
            baseIdentifier,
            valueField,
          }) => {
            const sourceData = compiledBaseSources.find(
              (baseSourceItem) => baseSourceItem.name === mergeDataSourceName,
            );
            if (!sourceData) {
              throw new Error(`Data source ${mergeDataSourceName} not found`);
            }
            return {
              data: sourceData.value,
              identifier,
              baseIdentifier,
              valueField,
            };
          },
        );

      return {
        name: mergeSourceName,
        value: {
          baseSource,
          mergeSources: sourcesData,
        },
        config: source,
        type: 'entity',
      };
    },
  );

  return [...compiledBaseSources, ...compiledMergedSources, ...urlDataSources];
};
